import {
  Box,
  IconButton,
  Skeleton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {ArrowBackIos, ArrowForwardIos} from '@mui/icons-material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useEffect, useRef, useState} from 'react';
import {useInView} from '@jobseeker/lotus/hooks/observer';
import type {
  AdsElementType,
  AdsEventInfo,
  AppState,
} from '@jobseeker/common-core/types';
import {
  TrackEventUBA,
  parseShowcaseUrl,
  getUbaPageName,
} from '@jobseeker/common-core/utils';
import {shallowEqual, useSelector} from 'react-redux';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';

export type PlatinumPartnersProps = {
  label?: string;
  adsEventInfo?: AdsEventInfo;
  slot: string;
};

const PlatinumPartners = ({
  slot,
  label,
  adsEventInfo,
}: PlatinumPartnersProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const [platinumAds, setPlatinumAds] = useState<AdsElementType[]>([]);
  const [adsLoaded, setAdsLoaded] = useState(false);

  const shouldLoad = Boolean(slot);
  useLoadAds(
    slot || '',
    setPlatinumAds,
    {},
    false,
    shouldLoad,
    setAdsLoaded,
    shouldLoad
  );

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollDistance = isMobile ? 250 : 370;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollDistance : scrollDistance,
        behavior: 'smooth',
      });
    }
  };

  const trackPlatinumPartnersEvent = (args: {
    type: 'view' | 'click';
    companyId?: string | number;
    companyName?: string;
    items?: AdsElementType[];
  }) => {
    const {type, companyId, companyName, items} = args;
    try {
      const refParam =
        new URL(window.location.href).searchParams.get('ref') || '';
      const source = 'Banner';

      if (type === 'view') {
        const list = items || platinumAds;
        const companyIDSArray = list
          .map(ad => ad?.companyId || parseShowcaseUrl(ad?.link))
          .filter(Boolean) as Array<string | number>;
        const companyNamesArray = list
          .map(ad => ad?.title)
          .filter(Boolean) as string[];

        TrackEventUBA('companyLogosView', 'view', {
          ref: refParam,
          source,
          companyIDSArray,
          companyNamesArray,
          page: getUbaPageName(),
          sessionUsername: sessionStorage.getItem('userId') || '',
        });
      } else {
        TrackEventUBA('companyLogosClick', 'click', {
          ref: refParam,
          source,
          companyID: companyId || '',
          companyName: companyName || '',
          page: getUbaPageName(),
          sessionUsername: sessionStorage.getItem('userId') || '',
        });
      }
    } catch (e) {
      console.error('UBA Event Tracking Error', e);
    }
  };

  useEffect(() => {
    if (!hasTrackedView && platinumAds.length) {
      trackPlatinumPartnersEvent({type: 'view', items: platinumAds});
      setHasTrackedView(true);
    }
  }, [platinumAds, hasTrackedView]);

  if (!platinumAds?.length && adsLoaded) return null;

  return (
    <Box
      position="relative"
      width="100%"
      data-testid={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
      id={`job_ads_banner_${label?.toLowerCase()?.replace(/ /g, '-')}`}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '15px',
          mt: '10px',
          mr: '12px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: {xss: '16px', md: '22px'},
            mt: 0,
            mb: 0,
          }}
        >
          {label}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
          }}
        >
          <IconButton
            aria-label="previous"
            size="small"
            onClick={() => scroll('left')}
            sx={{
              backgroundColor: 'white',
              color: '#666',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e0e0e0',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              width: 20,
              height: 20,
            }}
          >
            <ArrowBackIos sx={{fontSize: 12}} />
          </IconButton>
          <IconButton
            aria-label="next"
            size="small"
            onClick={() => scroll('right')}
            sx={{
              backgroundColor: 'white',
              color: '#666',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e0e0e0',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              width: 20,
              height: 20,
            }}
          >
            <ArrowForwardIos sx={{fontSize: 12}} />
          </IconButton>
        </Box>
      </Box>

      {!adsLoaded ? (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            px: 0,
            '&::-webkit-scrollbar': {display: 'none'},
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {Array.from({length: 3}).map((_, i) => (
            <Box
              key={`platinum-skeleton-${i}`}
              sx={{flex: '0 0 auto', mb: '30px'}}
            >
              <Skeleton
                variant="rectangular"
                width={isMobile ? 270 : 351}
                height={isMobile ? 90 : 117}
                sx={{
                  borderRadius: '15px',
                }}
              />
            </Box>
          ))}
        </Box>
      ) : platinumAds?.length > 0 ? (
        <Box
          ref={scrollRef}
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            px: 0,
            '&::-webkit-scrollbar': {display: 'none'},
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {platinumAds?.map((adObj: AdsElementType, i: number) => (
            <Box
              component="a"
              href={adObj.link || '#'}
              target="_blank"
              rel="noopener noreferrer nofollow"
              key={`platinum-ad-${i}`}
              sx={{flex: '0 0 auto', cursor: 'pointer', display: 'block'}}
              mb={'30px'}
              onClick={() => {
                trackPlatinumPartnersEvent({
                  type: 'click',
                  companyId: adObj.companyId,
                  companyName: adObj.title,
                });
              }}
            >
              <Image
                src={adObj.imageUrl || ''}
                alt={adObj.title || 'Platinum partner'}
                width={isMobile ? 270 : 351}
                height={isMobile ? 90 : 117}
                style={{
                  objectFit: 'cover',
                  width: isMobile ? '270px' : '351px',
                  height: isMobile ? '90px' : '117px',
                  borderRadius: '15px',
                }}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{height: 1, mb: 2}} />
      )}
    </Box>
  );
};

export default PlatinumPartners;
